import React, { createContext, useContext, useMemo, useState } from 'react';
import { useAxiosInstance, usePoMutation } from '@gov-nx/api';
import { useDownloadEvents, useMessageEvents } from '@gov-nx/core/events';
import { usePersonCommunicationData, useWizard } from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import type { CrzResponse, ServiceCode } from '@gov-nx/module/service';
import { GunListType } from '@gov-nx/module/service';
import type { SubmitMutationVariables } from '@gov-nx/api';
import { gunListSubmitMutation } from '@gov-nx/api';
import { getDocumentFirstFileId } from '../../../../../../core/hooks/src/Document/useDocumentFirstFile';
import { GunListEmptyStep } from '../GunListEmptyStep';
import { useCategorySelection, usePersonInfo } from '../hooks';
import { mapSelectedCategories } from '../mapSelectedCategories';
import { transformCrzDataToRecordsCompleteGunList } from '../transformCrzDataToRecordsCompleteGunList';
import type { GunListCompleteContextTypes } from './context.types';

export const GunListCompleteContext =
	createContext<GunListCompleteContextTypes | null>(null);

export interface GunListCompleteContextProps {
	children: React.ReactNode;
	code: ServiceCode;
	onSubmitSuccess?: () => void;
	crzResponse: CrzResponse;
}

export const GunListCompleteContextProvider = ({
	children,
	code,
	onSubmitSuccess,
	crzResponse,
}: GunListCompleteContextProps) => {
	const { t } = useLocale(code);
	const { axiosInstance } = useAxiosInstance();
	const personCommunicationData = usePersonCommunicationData();
	const { getPersonRecordsFromCrzResponse } = usePersonInfo(code);
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const { downloadDocument } = useDownloadEvents();

	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const crzRecords = transformCrzDataToRecordsCompleteGunList(crzResponse);

	const keyValuesPersonInfo = useMemo(
		() => getPersonRecordsFromCrzResponse(crzResponse),
		[getPersonRecordsFromCrzResponse, crzResponse]
	);

	const opravneniCategorySelection = useCategorySelection(
		crzRecords?.opravneni,
		[],
		true
	);
	const zbraneVyjimkyPovoleniCategorySelection = useCategorySelection(
		crzRecords?.zbraneVyjimkyPovoleni,
		[],
		true
	);

	const formStep1 = GunListEmptyStep();
	const formStep2 = GunListEmptyStep();
	const formStep3 = GunListEmptyStep();
	const formStep4 = GunListEmptyStep();

	const wizard = useWizard({
		steps: [formStep1, formStep2, formStep3, formStep4],
		onSubmit: async () => {
			const allSelected =
				opravneniCategorySelection.allSelected &&
				zbraneVyjimkyPovoleniCategorySelection.allSelected;

			if (allSelected) {
				submitMutation.mutate({ varianta: GunListType.UPLNY });
			} else {
				submitMutation.mutate({
					varianta: GunListType.CASTECNY,
					vybraneUdaje: mapSelectedCategories([
						...opravneniCategorySelection.getSelectedExpanded(),
						...zbraneVyjimkyPovoleniCategorySelection.getSelectedExpanded(),
					]),
				});
			}
		},
		onStepInvalid: (index, errors) => {
			if (index === 0) {
				personCommunicationData.onFormValidated(errors);
			}
		},
	});

	const submitMutation = usePoMutation({
		mutationFn: async (variables: SubmitMutationVariables) => {
			setIsSubmitLoading(true);
			return gunListSubmitMutation(axiosInstance, variables);
		},
		onError: () => {
			setIsSubmitLoading(false);
		},
		onSuccess: async (data) => {
			const souborId = await getDocumentFirstFileId(axiosInstance, {
				documentId: data.data,
			});

			if (!souborId) {
				setIsSubmitLoading(false);
				toastMessageError(t('soubor.stazeni-souboru-chyba'));
				return;
			}

			downloadDocument({
				documentId: data.data,
				souborId,
				callback: (_, success) => {
					setIsSubmitLoading(false);
					if (!success) {
						toastMessageError(t('soubor.stazeni-souboru-chyba'));
						return;
					}
					toastMessageSuccess(t('soubor.stazeni-souboru-uspech'));
				},
			});
			onSubmitSuccess?.();
		},
	});

	return (
		<GunListCompleteContext.Provider
			value={{
				wizard,
				keyValuesPersonInfo,
				personCommunicationData,
				opravneniCategorySelection,
				zbraneVyjimkyPovoleniCategorySelection,
				crzRecords,
				isSubmitLoading,
			}}>
			{children}
		</GunListCompleteContext.Provider>
	);
};

export const useGunListCompleteContext = () =>
	useContext(GunListCompleteContext) as GunListCompleteContextTypes;
